import { useContext, useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { ArrowBack, PlayArrow, Pause, Upload, VpnKey, Handshake, SwapHoriz } from '@mui/icons-material';
import * as cuitProfilesApi from '@/api/cuitProfiles';
import { markCuitManagementCertificateAccessReady } from '@/api/cuitManagementMandates';
import * as certificatesApi from '@/api/certificates';
import * as enablementApi from '@/api/enablement';
import { toApiError } from '@/api/client';
import { usePermissions } from '@/hooks/usePermissions';
import { LoadingScreen } from '@/components/LoadingScreen';
import { StatusChip } from '@/components/StatusChip';
import { CertificateInfo } from '@/components/CertificateInfo';
import { CertificateUploadDialog } from '@/components/CertificateUploadDialog';
import { CsrGenerationWizard } from '@/components/CsrGenerationWizard';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { PointsOfSaleTab } from '@/components/PointsOfSaleTab';
import { CuitManagementMandatesCard } from '@/components/CuitManagementMandatesCard';
import { ProductContext } from '@/auth/ProductContext';
import { formatCuit, formatDateTime } from '@/lib/formatters';
import { IVA_CONDITION_LABEL } from '@/lib/labels';
import type { TildeProduct } from '@/components/primitives/products';

type TabKey = 'overview' | 'certificate' | 'credentials' | 'pos';

export interface CuitProfileDetailSearch {
  tab?: TabKey;
  managementMandateId?: string;
}

const TAB_KEYS: readonly TabKey[] = ['overview', 'certificate', 'pos', 'credentials'];

export const Route = createFileRoute('/_authenticated/cuit-profiles/$id')({
  validateSearch: (search: Record<string, unknown>): CuitProfileDetailSearch => ({
    tab: TAB_KEYS.includes(search.tab as TabKey) ? (search.tab as TabKey) : undefined,
    managementMandateId: typeof search.managementMandateId === 'string' && search.managementMandateId.trim()
      ? search.managementMandateId.trim()
      : undefined,
  }),
  component: CuitProfileDetailPage,
});

function CuitProfileDetailPage(): JSX.Element {
  const { id } = Route.useParams();
  const search = Route.useSearch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const product = useContext(ProductContext);
  const perms = usePermissions();
  const tab = search.tab ?? 'overview';
  const openedFromMandate = Boolean(search.managementMandateId);
  const [openingRoom, setOpeningRoom] = useState(false);
  const operatorProduct: Exclude<TildeProduct, 'Admin'> | undefined =
    product?.activeProduct && product.activeProduct !== 'Admin' ? product.activeProduct : undefined;
  const [uploadOpen, setUploadOpen] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [confirmToggle, setConfirmToggle] = useState<'activate' | 'deactivate' | null>(null);
  const [toggling, setToggling] = useState(false);
  const [topError, setTopError] = useState<string | null>(null);

  const profileQ = useQuery({
    queryKey: ['cuit-profile', id],
    queryFn: () => cuitProfilesApi.getCuitProfile(id),
  });

  const certQ = useQuery({
    queryKey: ['active-cert', id],
    queryFn: () => certificatesApi.getActiveCertificate(id),
    enabled: Boolean(profileQ.data),
  });

  const markMandateReadyM = useMutation({
    mutationFn: () => markCuitManagementCertificateAccessReady(
      search.managementMandateId!,
      'Certificado validado desde el detalle del CUIT delegado',
    ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['study-cuit-management-requests'] });
      await queryClient.invalidateQueries({ queryKey: ['cuit-profile', id] });
      navigate({ to: '/clients' });
    },
    onError: (err) => setTopError(toApiError(err).message),
  });

  const handleCertificateConfigured = (): void => {
    setUploadOpen(false);
    setWizardOpen(false);
    queryClient.invalidateQueries({ queryKey: ['active-cert', id] });
    queryClient.invalidateQueries({ queryKey: ['cuit-profile', id] });
    if (openedFromMandate) {
      navigate({
        to: '/cuit-profiles/$id',
        params: { id },
        search: { tab: 'certificate', managementMandateId: search.managementMandateId },
        replace: true,
      });
      return;
    }
    navigate({
      to: '/cuit-profiles/$id',
      params: { id },
      search: { tab: 'pos' },
      replace: true,
    });
  };
  if (profileQ.isLoading) return <LoadingScreen />;
  if (profileQ.isError || !profileQ.data) {
    return (
      <Box sx={{ maxWidth: 720, mx: 'auto' }}>
        <Alert severity="error">No se pudo cargar el CUIT.</Alert>
      </Box>
    );
  }

  const profile = profileQ.data;

  const handleToggle = async (): Promise<void> => {
    if (!confirmToggle) return;
    setToggling(true);
    setTopError(null);
    try {
      if (confirmToggle === 'activate') {
        await cuitProfilesApi.activateCuitProfile(id);
      } else {
        await cuitProfilesApi.deactivateCuitProfile(id, 'Desactivado desde portal');
      }
      queryClient.invalidateQueries({ queryKey: ['cuit-profile', id] });
      queryClient.invalidateQueries({ queryKey: ['cuit-profiles'] });
      setConfirmToggle(null);
    } catch (err) {
      setTopError(toApiError(err).message);
    } finally {
      setToggling(false);
    }
  };

  // Abre (o crea, idempotente) la sala de habilitación del CUIT y navega a ella.
  const handleOpenRoom = async (): Promise<void> => {
    setOpeningRoom(true);
    setTopError(null);
    try {
      await enablementApi.createRoomForProfile(id);
      navigate({ to: '/cuit-profiles/$id/enablement', params: { id } });
    } catch (err) {
      setTopError(toApiError(err).message);
    } finally {
      setOpeningRoom(false);
    }
  };

  const refreshCert = (): void => {
    queryClient.invalidateQueries({ queryKey: ['active-cert', id] });
    queryClient.invalidateQueries({ queryKey: ['cuit-profile', id] });
  };

  return (
    <Box>
      <Button
        startIcon={<ArrowBack />}
        onClick={() => navigate({ to: '/cuit-profiles' })}
        sx={{ mb: 2 }}
      >
        Volver
      </Button>

      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'flex-start' }} sx={{ mb: 3, gap: 2 }}>
        <Box>
          <Typography variant="h4">{profile.razonSocial}</Typography>
          <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap' }}>
            <StatusChip kind="cuit-profile" value={profile.status} />
            <StatusChip kind="environment" value={profile.environment} />
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontFamily: 'var(--font-mono)' }}>
            CUIT {formatCuit(profile.cuit)}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
          {perms.canManageAfip ? (
            <Button
              variant="outlined"
              startIcon={<SwapHoriz />}
              onClick={() => {
                const target: typeof profile.environment =
                  profile.environment === 'Production' ? 'Homologation' : 'Production';
                navigate({
                  to: '/cuit-profiles/new',
                  search: {
                    cuit: profile.cuit,
                    razonSocial: profile.razonSocial,
                    condicionIva: profile.condicionIva,
                    street: profile.street,
                    city: profile.city,
                    province: profile.province,
                    postalCode: profile.postalCode,
                    ingresosBrutos: profile.ingresosBrutos ?? undefined,
                    fechaInicioActividades: profile.fechaInicioActividades ?? undefined,
                    environment: target,
                  },
                });
              }}
            >
              Registrar en {profile.environment === 'Production' ? 'Homologación' : 'Producción'}
            </Button>
          ) : null}
          {perms.canManageAfip || perms.canManageApi ? (
            <Button
              variant="outlined"
              startIcon={<Handshake />}
              onClick={handleOpenRoom}
              disabled={openingRoom}
            >
              Sala de habilitación
            </Button>
          ) : null}
          {profile.status === 'Inactive' || profile.status === 'PendingCertificate' ? (
            <Button
              variant="outlined"
              color="success"
              startIcon={<PlayArrow />}
              onClick={() => setConfirmToggle('activate')}
              disabled={profile.status === 'PendingCertificate'}
            >
              Activar
            </Button>
          ) : null}
          {profile.status === 'Active' ? (
            <Button
              variant="outlined"
              color="warning"
              startIcon={<Pause />}
              onClick={() => setConfirmToggle('deactivate')}
            >
              Desactivar
            </Button>
          ) : null}
        </Stack>
      </Stack>

      {topError ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setTopError(null)}>
          {topError}
        </Alert>
      ) : null}

      <Tabs
        value={tab}
        onChange={(_, value: TabKey) => {
          navigate({
            to: '/cuit-profiles/$id',
            params: { id },
            search: { tab: value },
            replace: true,
          });
        }}
        sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab value="overview" label="Resumen" />
        <Tab value="certificate" label="Certificado" />
        <Tab value="pos" label="Puntos de Venta" />
        <Tab value="credentials" label="Credenciales API" />
      </Tabs>

      {tab === 'overview' ? <OverviewTab profile={profile} /> : null}

      {tab === 'certificate' ? (
        <Box>
          {openedFromMandate ? (
            <Alert
              severity={certQ.data?.status === 'Active' ? 'success' : 'info'}
              sx={{ mb: 2 }}
              action={
                certQ.data?.status === 'Active' ? (
                  <Button
                    color="inherit"
                    size="small"
                    disabled={markMandateReadyM.isPending}
                    onClick={() => markMandateReadyM.mutate()}
                  >
                    Marcar listo y volver
                  </Button>
                ) : (
                  <Button component="a" href="/clients" color="inherit" size="small">
                    Volver a Mis cuentas
                  </Button>
                )
              }
            >
              {certQ.data?.status === 'Active'
                ? 'El certificado ya está activo para este CUIT. Podés cerrar la transferencia operativa desde acá y volver a Mis cuentas.'
                : 'Estás configurando el certificado para completar una transferencia operativa de CUIT. Cuando el certificado quede activo, volvé a Mis cuentas o marcá la transferencia lista desde esta pantalla.'}
            </Alert>
          ) : null}
          {certQ.isLoading ? (
            <LoadingScreen message="Cargando certificado…" />
          ) : certQ.data ? (
            <CertificateInfo
              cert={certQ.data}
              profileId={id}
              onChanged={refreshCert}
              onUploadNew={() => setUploadOpen(true)}
            />
          ) : (
            <Stack spacing={3} alignItems="stretch">
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <VpnKey sx={{ fontSize: 56, color: 'text.disabled', mb: 1 }} />
                <Typography variant="h6" gutterBottom>
                  Sin certificado activo
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 520, mx: 'auto' }}>
                  Elegí cómo querés configurar el certificado AFIP. Si todavía no lo tenés,
                  generalo desde el portal; si ya lo generaste por fuera, importalo directo.
                </Typography>
              </Box>
              <Box
                sx={{
                  display: 'grid',
                  gap: 2,
                  gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                }}
              >
                <Card variant="outlined" sx={{ display: 'flex', flexDirection: 'column' }}>
                  <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
                      <VpnKey color="primary" />
                      <Typography variant="subtitle1" fontWeight={600}>
                        Generar certificado
                      </Typography>
                    </Stack>
                    <Typography variant="body2" color="text.secondary" sx={{ flex: 1, mb: 2 }}>
                      Armamos el CSR por vos y te guiamos paso a paso por el flujo de AFIP.
                      Recomendado si todavía no tenés un certificado.
                    </Typography>
                    <Button
                      variant="contained"
                      fullWidth
                      onClick={() => setWizardOpen(true)}
                    >
                      Generar certificado
                    </Button>
                  </CardContent>
                </Card>
                <Card variant="outlined" sx={{ display: 'flex', flexDirection: 'column' }}>
                  <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
                      <Upload color="primary" />
                      <Typography variant="subtitle1" fontWeight={600}>
                        Importar certificado
                      </Typography>
                    </Stack>
                    <Typography variant="body2" color="text.secondary" sx={{ flex: 1, mb: 2 }}>
                      Si ya generaste el certificado por fuera, subí el archivo .pfx/.p12 o
                      los archivos cert + key por separado.
                    </Typography>
                    <Button
                      variant="outlined"
                      fullWidth
                      startIcon={<Upload />}
                      onClick={() => setUploadOpen(true)}
                    >
                      Importar certificado
                    </Button>
                  </CardContent>
                </Card>
              </Box>
            </Stack>
          )}
          <CertificateUploadDialog
            open={uploadOpen}
            profileId={id}
            onClose={() => setUploadOpen(false)}
            onUploaded={handleCertificateConfigured}
          />
          <CsrGenerationWizard
            open={wizardOpen}
            profileId={id}
            cuit={profile.cuit}
            environment={profile.environment}
            onClose={() => setWizardOpen(false)}
            onComplete={handleCertificateConfigured}
          />
        </Box>
      ) : null}

      {tab === 'credentials' ? (
        <Alert
          severity="info"
          action={(
            <Button color="inherit" size="small" onClick={() => navigate({ to: '/credenciales' })}>
              Administrar credenciales
            </Button>
          )}
        >
          Las credenciales ahora pertenecen a una aplicación y a un ambiente. En Production elegís el CUIT
          productivo al emitirlas; Sandbox no usa identidad fiscal.
        </Alert>
      ) : null}

      {tab === 'pos' ? (
        <PointsOfSaleTab
          profileId={id}
          cuit={profile.cuit}
          environment={profile.environment}
          ivaCondition={profile.condicionIva}
          certificateReady={certQ.data?.status === 'Active'}
          operatorProduct={operatorProduct}
        />
      ) : null}

      <ConfirmDialog
        open={confirmToggle !== null}
        title={confirmToggle === 'activate' ? '¿Activar CUIT?' : '¿Desactivar CUIT?'}
        description={
          confirmToggle === 'activate'
            ? 'El CUIT volverá a poder emitir facturas.'
            : 'Mientras esté desactivado, el CUIT no podrá emitir facturas.'
        }
        confirmLabel={confirmToggle === 'activate' ? 'Activar' : 'Desactivar'}
        destructive={confirmToggle === 'deactivate'}
        loading={toggling}
        onConfirm={handleToggle}
        onCancel={() => setConfirmToggle(null)}
      />
    </Box>
  );
}

interface OverviewTabProps {
  profile: import('@/api/types').CuitProfileResponse;
}

function OverviewTab({ profile }: OverviewTabProps): JSX.Element {
  return (
    <Stack spacing={2}>
      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
          Datos fiscales
          </Typography>
          <Stack spacing={1.5}>
          <Field label="CUIT" value={formatCuit(profile.cuit)} mono />
          <Field label="Razón social" value={profile.razonSocial} />
          <Field label="Condición IVA" value={IVA_CONDITION_LABEL[profile.condicionIva]} />
          <Field label="Domicilio fiscal" value={`${profile.street}, ${profile.city}, ${profile.province} (${profile.postalCode})`} />
          <Field label="Ingresos Brutos" value={profile.ingresosBrutos ?? 'No informado'} />
          <Field label="Inicio de actividades" value={profile.fechaInicioActividades ? new Date(`${profile.fechaInicioActividades}T00:00:00`).toLocaleDateString('es-AR') : 'No informado'} />
          <Field label="Creado" value={formatDateTime(profile.createdAt)} />
          <Field label="Actualizado" value={formatDateTime(profile.updatedAt)} />
          </Stack>
        </CardContent>
      </Card>
      <CuitManagementMandatesCard
        cuitProfileId={profile.id}
        cuit={profile.cuit}
        environment={profile.environment}
      />
    </Stack>
  );
}

function Field({ label, value, mono }: { label: string; value: string; mono?: boolean }): JSX.Element {
  return (
    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: { sm: 2 } }}>
      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 180 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontFamily: mono ? 'var(--font-mono)' : undefined }}>
        {value}
      </Typography>
    </Box>
  );
}
